import { memo, useEffect, useState, useCallback } from 'react'
import { Node, Edge } from 'reactflow'
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Checkbox } from "@/shared/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/shared/components/ui/select"
import { Switch } from "@/shared/components/ui/switch"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/shared/components/ui/sheet'
import { X } from "lucide-react"
import Editor from '@monaco-editor/react'
import type { ToolListItem, BuiltinToolInfo } from "@/modules/agent/types/tool"
import { EntryNodeConfig, AgentNodeConfig, StructuredAgentNodeConfig, BinderNodeConfig, RouterNodeConfig, OutputNodeConfig } from './node-configs'
import { SkillApiService } from '@/modules/agent/services/skill-api'

interface ModelOption {
  id: string
  name: string
  description: string
  providerId: string
  providerName: string
  configured?: boolean
}

interface ProviderGroup {
  provider: {
    id: string
    name: string
    configured: boolean
  }
  models: ModelOption[]
}

interface ConfigPanelProps {
  selectedNode: Node | null
  nodes: Node[]
  edges: Edge[]
  projectId: string
  modelsByProvider: ProviderGroup[]
  defaultModel: string
  tools: ToolListItem[]
  builtinTools: BuiltinToolInfo[]
  isLoadingTools: boolean
  userContextApiConfigured: boolean
  outputNodes: Node[]
  onClose: () => void
  onUpdateNodeData: (field: string, value: unknown) => void
  onOpenPromptEditor: (field: string, content: string, title: string, description: string) => void
  onMoveEdgeOrder: (sourceNodeId: string, currentIndex: number, direction: 'up' | 'down') => void
  // 提示词编辑器状态
  promptEditorOpen: boolean
  promptEditorContent: string
  promptEditorLanguage: 'markdown' | 'yaml'
  promptEditorField: string
  promptEditorTitle: string
  promptEditorDescription: string
  setPromptEditorOpen: (open: boolean) => void
  setPromptEditorContent: (content: string) => void
  setPromptEditorLanguage: (language: 'markdown' | 'yaml') => void
  setPromptEditorField: (field: string) => void
  setPromptEditorTitle: (title: string) => void
  setPromptEditorDescription: (description: string) => void
}

export const ConfigPanel = memo(({ 
  selectedNode,
  nodes,
  edges,
  projectId,
  modelsByProvider,
  defaultModel,
  tools,
  builtinTools,
  isLoadingTools,
  userContextApiConfigured,
  outputNodes,
  onClose,
  onUpdateNodeData,
  onOpenPromptEditor,
  onMoveEdgeOrder,
  // 提示词编辑器状态
  promptEditorOpen,
  promptEditorContent,
  promptEditorLanguage,
  promptEditorField,
  promptEditorTitle,
  promptEditorDescription,
  setPromptEditorOpen,
  setPromptEditorContent,
  setPromptEditorLanguage
}: ConfigPanelProps) => {
  
  // ClawHub installed skills for HermesAgent multi-select
  const [clawhubSkills, setClawhubSkills] = useState<any[]>([])

  useEffect(() => {
    if (selectedNode?.type === "hermes_agent") {
      SkillApiService.getInstalledClawhub().then(r => {
        if (r.success) setClawhubSkills(r.data.skills || [])
      }).catch(() => {})
    }
  }, [selectedNode?.id, selectedNode?.type])

  const toggleSkill = useCallback((skillName: string, checked: boolean) => {
    const current = (selectedNode?.data.selected_skills as string[]) || []
    const next = checked
      ? [...current, skillName]
      : current.filter((s: string) => s !== skillName)
    onUpdateNodeData('selected_skills', next)
  }, [selectedNode?.data.selected_skills, onUpdateNodeData])

  if (!selectedNode) return null

  return (
    <>
    <aside className="w-80 border-l bg-white flex flex-col shrink-0 shadow-lg">
      <div className="p-4 border-b flex justify-between items-center bg-slate-50">
        <span className="font-medium text-sm">节点配置</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {selectedNode.type === "entry" && (
          <EntryNodeConfig
            selectedNode={selectedNode}
            onUpdateNodeData={onUpdateNodeData}
            onOpenPromptEditor={onOpenPromptEditor}
          />
        )}

        {selectedNode.type === "agent" && (
          <AgentNodeConfig
            selectedNode={selectedNode}
            edges={edges}
            outputNodes={outputNodes}
            modelsByProvider={modelsByProvider}
            defaultModel={defaultModel}
            tools={tools}
            builtinTools={builtinTools}
            isLoadingTools={isLoadingTools}
            userContextApiConfigured={userContextApiConfigured}
            onUpdateNodeData={onUpdateNodeData}
            onOpenPromptEditor={onOpenPromptEditor}
          />
        )}

        {selectedNode.type === "binder" && (
          <BinderNodeConfig
            selectedNode={selectedNode}
            nodes={nodes}
            edges={edges}
            onUpdateNodeData={onUpdateNodeData}
            onMoveEdgeOrder={onMoveEdgeOrder}
          />
        )}

        {/* v16.0: HermesAgent — minimal config, skill-driven */}
        {selectedNode.type === "hermes_agent" && (
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800 font-medium">🧠 原生智能体</p>
              <p className="text-xs text-purple-600 mt-1">
                Skill 驱动，不需要配置 Prompt。在技能管理页面安装 ClawHub 技能后勾选即可。
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="hermes-model" className="text-sm">模型</Label>
                <Select value={selectedNode.data.model || defaultModel}
                  onValueChange={(v) => onUpdateNodeData('model', v)}>
                  <SelectTrigger id="hermes-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {modelsByProvider.map((group) => (
                      <SelectGroup key={group.provider.id}>
                        <SelectLabel>{group.provider.name}</SelectLabel>
                        {group.models.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">最大步数</Label>
                <Input type="number" min={1} max={100}
                  value={selectedNode.data.max_steps ?? 30}
                  onChange={(e) => onUpdateNodeData('max_steps', parseInt(e.target.value) || 30)} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="global-skills-switch" className="text-sm">
                    加载全局 Skills
                  </Label>
                  <p className="text-xs text-slate-500 max-w-[200px]">
                    加载 ~/.hermes/skills/ 中的通用技能
                  </p>
                </div>
                <Switch id="global-skills-switch"
                  checked={selectedNode.data.global_skills !== false}
                  onCheckedChange={(checked) => onUpdateNodeData('global_skills', checked)} />
              </div>

              {/* ClawHub 技能选择 */}
              <div className="border-t pt-3 mt-1">
                <Label className="text-sm mb-2 block">ClawHub 技能</Label>
                {clawhubSkills.length === 0 ? (
                  <p className="text-xs text-slate-400 mb-2">
                    暂无已安装的技能。前往
                    <a href="/skills" className="text-purple-500 underline mx-1">技能管理</a>
                    页面搜索并安装 ClawHub 社区技能。
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mb-2">
                      选择此节点可用的 ClawHub 技能（在技能管理页面安装更多）
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {clawhubSkills.map((s: any) => {
                        const selected = ((selectedNode.data.selected_skills as string[]) || []).includes(s.name)
                        return (
                          <label key={s.name} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={(c) => toggleSkill(s.name, c === true)}
                            />
                            <div className="min-w-0">
                              <span className="text-sm">{s.name}</span>
                              {s.description && (
                                <p className="text-xs text-slate-400 truncate">{s.description}</p>
                              )}
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedNode.type === "router" && (
          <RouterNodeConfig
            selectedNode={selectedNode}
            nodes={nodes}
            modelsByProvider={modelsByProvider}
            defaultModel={defaultModel}
            onUpdateNodeData={onUpdateNodeData}
            onOpenPromptEditor={onOpenPromptEditor}
          />
        )}

        {selectedNode.type === "output" && (
          <OutputNodeConfig
            selectedNode={selectedNode}
            onUpdateNodeData={onUpdateNodeData}
          />
        )}

        {/* TODO: 添加其他节点类型的配置组件 */}

        {selectedNode.type === "structured_agent" && (
          <StructuredAgentNodeConfig
            selectedNode={selectedNode}
            edges={edges}
            outputNodes={outputNodes}
            projectId={projectId}
            modelsByProvider={modelsByProvider}
            defaultModel={defaultModel}
            tools={tools}
            builtinTools={builtinTools}
            isLoadingTools={isLoadingTools}
            userContextApiConfigured={userContextApiConfigured}
            onUpdateNodeData={onUpdateNodeData}
            onOpenPromptEditor={onOpenPromptEditor}
          />
        )}

        {/* 陪伴智能体 - 使用通用 AgentNodeConfig */}
        {selectedNode.type === "companion_agent" && (
          <AgentNodeConfig
            selectedNode={selectedNode}
            edges={edges}
            outputNodes={outputNodes}
            modelsByProvider={modelsByProvider}
            defaultModel={defaultModel}
            tools={tools}
            builtinTools={builtinTools}
            isLoadingTools={isLoadingTools}
            userContextApiConfigured={userContextApiConfigured}
            onUpdateNodeData={onUpdateNodeData}
            onOpenPromptEditor={onOpenPromptEditor}
          />
        )}


        {selectedNode.type === "function" && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">Function 节点配置待迁移</p>
          </div>
        )}

        {selectedNode.type === "condition" && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">Condition 节点配置待迁移</p>
          </div>
        )}
      </div>
    </aside>

    {/* System Prompt 编辑器 - 从右侧滑出的抽屉 */}
    <Sheet open={promptEditorOpen} onOpenChange={setPromptEditorOpen}>
      <SheetContent 
        side="right" 
        className="w-[60vw] max-w-[960px] sm:max-w-[960px] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>
            {promptEditorTitle || (
              promptEditorField.startsWith('user_prompt_templates.') 
                ? `User Prompt 模板编辑器 - ${
                    promptEditorField === 'user_prompt_templates.user' ? '用户输入' :
                    promptEditorField === 'user_prompt_templates.iot_data' ? 'IoT数据' :
                    promptEditorField === 'user_prompt_templates.schedule' ? '定时任务' :
                    '未知'
                  }`
                : promptEditorField === 'identity_rules' 
                  ? 'Identity Rules 编辑器'
                  : 'System Prompt 编辑器'
            )}
          </SheetTitle>
          <SheetDescription>
            {promptEditorDescription || (
              promptEditorField.startsWith('user_prompt_templates.') 
                ? '编辑用户提示词模板，可使用变量如 {mood}, {message} 等'
                : promptEditorField === 'identity_rules' 
                  ? '编辑身份模拟规则，支持 Markdown 和 YAML 格式'
                  : '编辑 Agent 的系统提示词，支持 Markdown 和 YAML 格式'
            )}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">语法高亮:</span>
            <Button
              variant={promptEditorLanguage === 'markdown' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPromptEditorLanguage('markdown')}
            >
              Markdown
            </Button>
            <Button
              variant={promptEditorLanguage === 'yaml' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPromptEditorLanguage('yaml')}
            >
              YAML
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <Editor
            height="100%"
            language={promptEditorLanguage}
            value={promptEditorContent}
            onChange={(value) => setPromptEditorContent(value || '')}
            theme="vs-light"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              wordWrap: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              padding: { top: 16, bottom: 16 },
            }}
          />
        </div>

        <SheetFooter className="px-6 py-4 border-t bg-slate-50/50">
          <Button variant="outline" onClick={() => setPromptEditorOpen(false)}>
            取消
          </Button>
          <Button onClick={() => {
            onUpdateNodeData(promptEditorField, promptEditorContent)
            setPromptEditorOpen(false)
          }}>
            保存
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  </>
  )
})

ConfigPanel.displayName = 'ConfigPanel'